/**
 * Same concept as {@link Object.assign}, but where {@link Object.assign} gets
 * the values and writes them to the target object, this assigns the full
 * property descriptors.
 *
 * This is most useful for preserving getters and setters as getters/setters.
 * {@link Object.assign} only copies the _current_ values returned by the
 * getters to the target object.
 */
export function assignProperties<T, S extends readonly any[]>(
  target: T,
  ...sources: S
): T & UnionToIntersection<S[number]>
export function assignProperties(
  target: object,
  ...sources: readonly unknown[]
): object
export function assignProperties(
  target: object,
  ...sources: readonly unknown[]
): any {
  for (const source of sources) {
    const sourcePrototypeChain = [source]
    let sourcePrototype: unknown = source
    while (
      (sourcePrototype = Object.getPrototypeOf(sourcePrototype)) &&
      sourcePrototype !== Object.prototype
    ) {
      // Adds all non-standard prototypes to be processed
      sourcePrototypeChain.push(sourcePrototype)
    }
    // Iterates down the prototype chain, starting at the root
    for (let i = sourcePrototypeChain.length - 1; i >= 0; i--) {
      sourcePrototype = sourcePrototypeChain[i]
      for (const key of [
        // NOTE: these are needed to get all properties, including non-enumerable ones
        ...Object.getOwnPropertyNames(sourcePrototype),
        ...Object.getOwnPropertySymbols(sourcePrototype),
      ]) {
        // We get the property descriptor so we can assign it to the target
        const propertyDescriptor = Object.getOwnPropertyDescriptor(
          sourcePrototype,
          key,
        )
        if (!propertyDescriptor) {
          // This should never occur, but if it does, there is nothing to do here
          continue
        }
        // Must be configurable to allow future Object.defineProperty calls
        // (e.g. by other sources or prototypes)
        propertyDescriptor.configurable = true
        Object.defineProperty(target, key, propertyDescriptor)
      }
    }
  }
  return target
}
